import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { ToastContainer } from 'react-toastify';
import { ReactFlowProvider } from '@xyflow/react';
import axios from "axios";

import CustomCampaignShow from "./CustomCampaignShow";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const CustomCampaignShowContext = createContext();

export function useCustomCampaignShowContext() {
  return useContext(CustomCampaignShowContext);
}

const CustomCampaignShowProvider = (props) => {
  const {
    csrfToken,
    user,
    team,
    beefreeToken,
    sendDomains,
    twilioMessagingServices,
    customCampaignBaseUrl,
    venueOwnership,
    smsStatus,
  } = props;

  const [customCampaign, setCustomCampaignState] = useState(props.customCampaign);
  const [buildPayload, setBuildPayload] = useState(null);
  const [building, setBuilding] = useState(false);
  const [showBeefreeComposer, setShowBeefreeComposer] = useState(false);
  const [showTwilioComposer, setShowTwilioComposer] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeFormState, setSelectedNodeFormState] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [filtersToDelete, setFiltersToDelete] = useState([]);
  const [targetEdgeInternalId, setTargetEdgeInternalId] = useState(0);
  const lastValidatedFormStateRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const setCustomCampaign = (updatedCustomCampaign) => {
    if (!updatedCustomCampaign) {
      return;
    }

    setCustomCampaignState(updatedCustomCampaign);
  };

  useEffect(() => {
    setSelectedNodeFormState(_.cloneDeep(selectedNode));
    setFiltersToDelete([]);
    setSaveDisabled(true);
  }, [selectedNode]);

  const hasFilterGroupAttributes = Object.keys(selectedNodeFormState?.filter_group || {}).length > 0;

  const updateSelectedNodeData = (onSuccess=()=>{}) => {
    setSaving(true);

    const payload = {
      workflow_node: {
        node_data: selectedNodeFormState.node_data,
      }
    };

    if (hasFilterGroupAttributes) {
      payload.workflow_node.filter_group_attributes = selectedNodeFormState.filter_group;
    }

    if (filtersToDelete.length > 0) {
      payload.workflow_node.filter_group_attributes = {
        ...selectedNodeFormState.filter_group,
        filters_attributes: selectedNodeFormState.filter_group.filters_attributes.concat(
          filtersToDelete.map(id => ({ id, _destroy: true }))
        ),
      }
    }

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    const saveNode = (headers) => axios.patch(
      `${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`,
      payload,
      headers ? { headers } : {}
    );

    saveNode()
      .then(response => (
        response.data && response.data.custom_campaign
          ? response
          : saveNode({ "Cache-Control": "no-cache" })
      ))
      .then(({ data }) => {
        const { workflow_node, custom_campaign } = data || {};

        if (!workflow_node || !custom_campaign) {
          toastError("Error updating node");
          return;
        }

        setCustomCampaign(custom_campaign);
        setSelectedNode(workflow_node);
        setFiltersToDelete([]);
        toastSuccess("Updated successfully");
        onSuccess();
      })
      .catch(() => {
        toastError("Error updating node");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const checkSaveDisabled = _.debounce((selectedNodeFormState, filtersToDelete) => {
    if (!selectedNode) {
      return;
    }

    if (_.isEqual(lastValidatedFormStateRef.current, selectedNodeFormState)) {
      return;
    }

    const unpersistedChanges = !_.isEqual(selectedNodeFormState.node_data, selectedNode.node_data) ||
      !_.isEqual(selectedNodeFormState.filter_group?.match_condition, (selectedNode.filter_group || {}).match_condition) ||
      !_.isEqual(selectedNodeFormState.filter_group?.filters_attributes, (selectedNode.filter_group || {}).filters_attributes) ||
      filtersToDelete.length > 0;

    if (unpersistedChanges) {
      const payload = {
        workflow_node: {
          node_data: selectedNodeFormState.node_data,
        }
      };

      if (hasFilterGroupAttributes) {
        payload.workflow_node.filter_group_attributes = selectedNodeFormState.filter_group;
      }

      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.post(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}/validate_node_data`, payload)
        .then(({ data: { errors, node_data_options, filter_group } }) => {
          let newNodeFilterGroup = filter_group;

          if (filtersToDelete.length > 0) {
            newNodeFilterGroup.filters_attributes = newNodeFilterGroup.filters_attributes?.filter(filter => !filtersToDelete.includes(filter.id)) || [];
          }

          if (hasFilterGroupAttributes) {
            newNodeFilterGroup.filters_attributes = newNodeFilterGroup.filters_attributes?.map((filter, index) => {
              if (filter.id) {
                return filter;
              } else {
                return {
                  ...filter,
                  tempId: selectedNodeFormState.filter_group.filters_attributes[index].tempId,
                };
              }
            });
          }

          if (customCampaign.type === "CustomCampaigns::Template") {
            setSaveDisabled(false);
          } else {
            const filtersErrors = newNodeFilterGroup.filters_attributes?.some(filter =>
              filter.errors && Object.keys(filter.errors).length > 0
            ) || false;

            setSaveDisabled(Object.keys(errors).length > 0 || filtersErrors);
          }

          const newFormState = {
            ...selectedNodeFormState,
            node_data_options: node_data_options,
            filter_group: newNodeFilterGroup,
            workflow_errors: errors,
          };

          lastValidatedFormStateRef.current = _.cloneDeep(newFormState);

          setSelectedNodeFormState(newFormState);
        })
        .catch(error => {
          console.error("error", error);
        });
    } else {
      lastValidatedFormStateRef.current = _.cloneDeep(selectedNodeFormState);

      setSaveDisabled(true);
    }
  }, 100);

  useEffect(() => {
    checkSaveDisabled(selectedNodeFormState, filtersToDelete);
  }, [selectedNodeFormState, filtersToDelete]);

  const value = {
    csrfToken,
    user,
    team,
    venueOwnership,
    buildPayload,
    setBuildPayload,
    building,
    setBuilding,
    showBeefreeComposer,
    setShowBeefreeComposer,
    showTwilioComposer,
    setShowTwilioComposer,
    customCampaign,
    setCustomCampaign,
    targetEdgeInternalId,
    setTargetEdgeInternalId,
    selectedNode,
    setSelectedNode,
    updateSelectedNodeData,
    saveDisabled,
    setSaveDisabled,
    saving,
    filtersToDelete,
    setFiltersToDelete,
    selectedNodeFormState,
    setSelectedNodeFormState,
    lastValidatedFormStateRef,
    beefreeToken,
    sendDomains,
    twilioMessagingServices,
    customCampaignBaseUrl,
    smsStatus,
  };

  return (
    <CustomCampaignShowContext.Provider value={value}>
      {props.children}
    </CustomCampaignShowContext.Provider>
  );
};

const CustomCampaignShowContainer = (props) => (
  <CustomCampaignShowProvider {...props}>
    <ToastContainer icon={false} />
    <ReactFlowProvider>
      <CustomCampaignShow />
    </ReactFlowProvider>
  </CustomCampaignShowProvider>
);

export default CustomCampaignShowContainer;
